import type { Locale } from './contracts/primitives';

/** Minimal shape shared by `ArtistRef`, `ArtistDTO` and CMS artist rows. */
export interface ArtistNameLike {
  name: string;
}

/**
 * Artist line for a song with one or more artists (ordered as in `song_artists.position`):
 * `"Queen"`, `"Juan Luis Guerra & Maná"`, `"A, B & C"`. The separator is the same in both
 * locales (music credits use `&` in Spanish too); `locale` is accepted so callers do not
 * need to change when a locale-specific separator is introduced.
 *
 * Accepts the catalog shape (`SongDTO.artists`), plain names, or the already-joined
 * `artistNames` string of an RTDB queue item (docs/02 §2) — that one is split back on `, `
 * so a queue row reads exactly like the same song everywhere else.
 */
export function formatArtists(
  artists: readonly (ArtistNameLike | string)[] | string | null | undefined,
  _locale: Locale = 'es',
): string {
  const list = typeof artists === 'string' ? [artists] : (artists ?? []);
  const names: string[] = [];
  for (const entry of list) {
    const raw = typeof entry === 'string' ? entry : entry.name;
    for (const part of raw.split(',')) {
      const name = part.trim();
      if (name) names.push(name);
    }
  }
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** `m:ss` for a song duration (`null`/`undefined` → empty string, 0 → `0:00`). */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Total length of a list, as on the playlist header ("1 h 38 min", "42 min"). Empty when
 * there is nothing to sum.
 */
export function formatTotalDuration(ms: number | null | undefined, _locale: Locale = 'es'): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '';
  const totalMin = Math.round(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/**
 * Build id for a footer line. CI passes the **full** commit SHA as `APP_VERSION`
 * (`.github/workflows/deploy.yml` → `${{ github.sha }}`), and 40 hex characters wrapped the
 * phone's profile footer onto two lines (QA 2026-09-15, UX-128). Anything that looks like a
 * commit SHA is shortened to the usual 7-character prefix; a human version ("1.4.0", "dev")
 * is left exactly as it is.
 */
export function shortVersion(version: string | null | undefined): string {
  const value = (version ?? '').trim();
  return /^[0-9a-f]{12,}$/i.test(value) ? value.slice(0, 7) : value;
}
